import React, { useState } from 'react';
import { BellIcon, HelpCircleIcon, LinkIcon, MenuIcon, FlameIcon, InfoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import SupportDropdown from './SupportDropdown';
import NotificationDropdown from './NotificationDropdown';
import MobileMenu from './MobileMenu';
interface HeaderProps {
  onStartVideoTour?: () => void;
}
const Header: React.FC<HeaderProps> = ({
  onStartVideoTour
}) => {
  const notificationCount = 3;
  const userName = 'John';
  const [showJoinCode, setShowJoinCode] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentStreak = 7;
  // Logic for recommended activity tooltip
  const assessmentsSharedCount = 1; // Mock count - set to 1 to trigger tooltip
  const currentDate = new Date();
  const dayOfMonth = currentDate.getDate();
  const isAfter15th = dayOfMonth > 15;
  // For testing: Always show if assessments < 2 (remove date check temporarily)
  const showRecommendation = assessmentsSharedCount < 2;
  console.log('Tooltip Debug:', {
    assessmentsSharedCount,
    dayOfMonth,
    isAfter15th,
    showRecommendation
  });
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };
  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Joining with code:', joinCode);
    setShowJoinCode(false);
    setJoinCode('');
  };
  return <>
      <div className="bg-[#00B75F] sticky top-0 z-50" data-id="element-1521">
        <header className="px-4 py-3 md:py-2.5 md:px-6" data-id="element-1522">
          <div className="flex items-center justify-between gap-3 md:gap-6 max-w-7xl mx-auto" data-id="element-1523">
            {/* Mobile Menu Button & Logo */}
            <div className="flex items-center gap-2 md:gap-3" data-id="element-1524">
              <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Open menu" data-id="element-1525">
                <MenuIcon size={24} className="text-white" data-id="element-1526" />
              </button>
              <div className="flex items-center gap-2" data-id="element-1527">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qarEDkTctoyPPpJPJss7Dk/AA_-_horizontal_v_-_light_green_bg2x.png" alt="Ainstein Logo" className="h-8 md:h-12 w-auto" data-id="element-1528" />
              </div>
            </div>
            {/* Center: Greeting & Streak - Desktop Only */}
            <div className="hidden lg:flex items-center gap-4 flex-1 justify-center" data-id="element-1529">
              <span className="text-white font-semibold text-base" data-id="element-1530">
                {getGreeting()}, {userName}
              </span>
              <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-xl border-2 border-white/20 shadow-lg" data-id="element-1531">
                <motion.div animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1532">
                  <FlameIcon size={20} className="text-[#FF9D33] fill-[#FF9D33]" data-id="element-1533" />
                </motion.div>
                <div className="flex items-baseline gap-1" data-id="element-1534">
                  <span className="text-white text-xl font-bold" data-id="element-1535">
                    {currentStreak}
                  </span>
                  <span className="text-white/80 text-sm" data-id="element-1536">week streak</span>
                </div>
                <motion.div animate={{
                rotate: [0, 5, -5, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="text-xl" data-id="element-1537">
                  🔥
                </motion.div>
              </motion.div>
            </div>
            {/* Mobile Streak Badge - Compact Version */}
            <div className="flex lg:hidden items-center gap-1.5 px-2.5 py-1.5 bg-white/15 backdrop-blur-sm rounded-lg border border-white/20" data-id="element-1538">
              <FlameIcon size={14} className="text-[#FF9D33] fill-[#FF9D33]" data-id="element-1539" />
              <span className="text-white text-sm font-bold" data-id="element-1540">
                {currentStreak}
              </span>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2 md:gap-3 ml-auto" data-id="element-1541">
              {/* Join Code Button/Input - Hidden on Mobile */}
              {!showJoinCode ? <button onClick={() => setShowJoinCode(true)} className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-200 text-sm font-medium min-h-[44px]" data-id="element-1542">
                  <LinkIcon size={16} data-id="element-1543" />
                  <span className="hidden md:inline" data-id="element-1544">Join Code</span>
                </button> : <form onSubmit={handleJoinSubmit} className="hidden sm:flex items-center gap-2" data-id="element-1545">
                  <input type="text" placeholder="Enter code" value={joinCode} onChange={e => setJoinCode(e.target.value)} className="w-28 md:w-32 px-3 py-2 text-sm bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20 min-h-[44px]" autoFocus data-id="element-1546" />
                  <button type="submit" className="px-3 py-2 bg-white text-[#00B75F] rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors min-h-[44px]" data-id="element-1547">
                    Join
                  </button>
                  <button type="button" onClick={() => {
                setShowJoinCode(false);
                setJoinCode('');
              }} className="p-2 hover:bg-white/10 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" data-id="element-1548">
                    <span className="text-white text-sm" data-id="element-1549">✕</span>
                  </button>
                </form>}
              <div className="hidden md:block" data-id="element-1550">
                <SupportDropdown onStartVideoTour={onStartVideoTour} trigger={<button className="p-2.5 rounded-xl hover:bg-white/10 transition-all duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Support and feedback" data-id="element-1552">
                      <HelpCircleIcon size={20} className="text-white/90" data-id="element-1553" />
                    </button>} data-id="element-1551" />
              </div>
              <Tooltip.Provider delayDuration={300} data-id="element-1554">
                <Tooltip.Root data-id="element-1555">
                  <Tooltip.Trigger asChild data-id="element-1556">
                    <div data-id="element-1557">
                      <NotificationDropdown notificationCount={notificationCount} trigger={<button className="p-2 md:p-2.5 rounded-xl hover:bg-white/10 transition-all duration-200 relative min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Notifications" data-id="element-1559">
                            <BellIcon size={18} className="md:w-5 md:h-5 text-white/90" data-id="element-1560" />
                            {notificationCount > 0 && <span className="absolute top-1 right-1 md:top-1.5 md:right-1.5 bg-[#FF6CA1] w-2 h-2 rounded-full border-2 border-[#00B75F] animate-pulse" data-id="element-1561"></span>}
                          </button>} data-id="element-1558" />
                    </div>
                  </Tooltip.Trigger>
                  {showRecommendation && <Tooltip.Portal data-id="element-1562">
                      <Tooltip.Content className="bg-white px-3 py-2 rounded-lg shadow-xl border border-gray-200 max-w-xs z-[9999]" sideOffset={8} side="bottom" align="end" data-id="element-1563">
                        <div className="flex items-start gap-2" data-id="element-1564">
                          <InfoIcon size={16} className="text-[#00B75F] mt-0.5 flex-shrink-0" data-id="element-1565" />
                          <div data-id="element-1566">
                            <p className="text-xs font-semibold text-gray-900 mb-0.5" data-id="element-1567">
                              Recommended Activity
                            </p>
                            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1568">
                              Aim for 1 assessment per week or 2 per month to
                              keep students engaged.
                            </p>
                          </div>
                        </div>
                        <Tooltip.Arrow className="fill-white" data-id="element-1569" />
                      </Tooltip.Content>
                    </Tooltip.Portal>}
                </Tooltip.Root>
              </Tooltip.Provider>
              <div className="ml-2 pl-2 md:ml-3 md:pl-3 border-l border-white/20" data-id="element-1570">
                <button className="flex items-center gap-2 md:gap-3 group min-h-[44px]" aria-label="User profile" data-id="element-1571">
                  <div className="w-9 h-9 md:w-9 md:h-9 bg-white rounded-xl flex items-center justify-center text-[#00B75F] font-bold shadow-md group-hover:scale-105 transition-transform duration-200 text-sm" data-id="element-1572">
                    {userName.charAt(0)}
                  </div>
                  <div className="hidden lg:block text-left" data-id="element-1573">
                    <p className="text-sm font-semibold text-white" data-id="element-1574">
                      {userName}
                    </p>
                    <p className="text-xs text-white/70" data-id="element-1575">View profile</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </header>
      </div>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} data-id="element-1576" />
    </>;
};
export default Header;